import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import type {
  RenovationMediaPinDto,
  RenovationPinCategory,
  RenovationPinStatus,
} from '@cc/types';

type PinFields = {
  xNorm: number;
  yNorm: number;
  comment: string;
  linkedItemId: string | null;
  category: RenovationPinCategory;
  status: RenovationPinStatus;
  dueDate: string | null;
  costImpact: number | null;
  assignee: string | null;
};

/**
 * Pins on a single media row (Frame.io-style annotations). Used by the hero
 * image overlay. Pins live with the media id, so swapping the room's hero
 * keeps each image's pins separate.
 */
export function useMediaPins(args: {
  projectId: string;
  roomId: string | null;
  mediaId: string | null;
}) {
  const queryClient = useQueryClient();
  const { projectId, roomId, mediaId } = args;

  const list = useQuery({
    queryKey: qk.mediaPins(mediaId ?? ''),
    queryFn: () => api.listMediaPins(projectId, roomId!, mediaId!),
    enabled: Boolean(roomId && mediaId),
  });

  function invalidate() {
    if (!mediaId) return;
    void queryClient.invalidateQueries({ queryKey: qk.mediaPins(mediaId) });
  }

  const create = useMutation({
    mutationFn: (input: Partial<PinFields> & Pick<PinFields, 'xNorm' | 'yNorm' | 'comment'>) => {
      if (!roomId || !mediaId) throw new Error('no media');
      return api.createMediaPin(projectId, roomId, mediaId, input);
    },
    onSuccess: () => invalidate(),
    onError: () => toast.error('Failed to add pin.'),
  });

  const update = useMutation({
    mutationFn: (args: { pinId: string; patch: Partial<PinFields> }) => {
      if (!roomId || !mediaId) throw new Error('no media');
      return api.updateMediaPin(projectId, roomId, mediaId, args.pinId, args.patch);
    },
    onSuccess: () => invalidate(),
    onError: () => toast.error('Failed to update pin.'),
  });

  const remove = useMutation({
    mutationFn: (pinId: string) => {
      if (!roomId || !mediaId) throw new Error('no media');
      return api.deleteMediaPin(projectId, roomId, mediaId, pinId);
    },
    onSuccess: () => invalidate(),
    onError: () => toast.error('Failed to delete pin.'),
  });

  return {
    pins: (list.data?.pins ?? []) as RenovationMediaPinDto[],
    isLoading: list.isLoading,
    invalidate,
    create,
    update,
    remove,
  };
}
